import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { clearAuthToken, getAuthToken } from '../utils/authStorage'
import {
  clearLucasWorkspaceSessionMarkers,
  markLucasForceAskLanding,
} from '../utils/lucasWorkspaceTabSession'

interface AuthState {
  /** False until zustand persist finishes reading localStorage — avoids auth flash on load */
  _hasHydrated: boolean
  isLoggedIn: boolean
  user: {
    id: string
    email: string
    firstName: string
    lastName: string
    role: 'student' | 'admin' | 'tutor' | 'school' | 'affiliate'
    schoolName?: string
    schoolId?: string
    // Photo fields
    photoUrl?: string
    photoFilename?: string
    photoSize?: number
    photoMimeType?: string
    photoUploadedAt?: string
    photo?: string
    timezone?: string
    preferredLocale?: string
    voicePreference?: string
    lastSeen?: string
    emailVerified?: boolean
    onboardingCompleted?: boolean
    needsOnboarding?: boolean
    hasGoogle?: boolean
  } | null
  login: (userData: AuthState['user']) => void
  logout: () => void
  updateUser: (userData: Partial<AuthState['user']>) => void
  reset: () => void
  setHasHydrated: (hydrated: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      _hasHydrated: false,
      isLoggedIn: false, // Start as not logged in
      user: null, // Start with no user
      setHasHydrated: (hydrated) => set({ _hasHydrated: hydrated }),
      login: (userData) => {
        if (userData) {
          console.log('Auth Store: Logging in user:', userData)
          console.log('Auth Store: Photo URL being stored:', userData.photoUrl)
          console.log('Auth Store: Photo field being stored:', userData.photo)
          set({ isLoggedIn: true, user: userData })
          // Store role in localStorage for DashboardLayout compatibility
          localStorage.setItem('userRole', userData.role)
          // Next workspace open → ask landing (login / registration).
          markLucasForceAskLanding()
          console.log('Auth Store: Login complete, new state:', get())
          console.log('Auth Store: Token present:', Boolean(getAuthToken()))
        }
      },
      logout: () => {
        console.log('Auth Store: Logging out user')
        console.log('Auth Store: Token before logout:', Boolean(getAuthToken()))
        
        // Clear state immediately to prevent UI flicker
        set({ isLoggedIn: false, user: null })
        
        clearAuthToken()
        clearLucasWorkspaceSessionMarkers()
        
        // Clear persisted auth and role keys
        const keysToRemove = [
          'userRole', 'adminRole', 'tutorRole', 'schoolRole',
          'auth-storage', 'activeChat'
        ]
        
        keysToRemove.forEach(key => {
          localStorage.removeItem(key)
        })
        
        console.log('Auth Store: Logout complete, new state:', get())
        console.log('Auth Store: Token after logout:', Boolean(getAuthToken()))
      },
      updateUser: (userData) => set((state) => ({
        user: state.user ? { ...state.user, ...userData } : null
      })),
      reset: () => {
        console.log('Auth Store: Resetting to initial state')
        set({ isLoggedIn: false, user: null })
        // Clear all localStorage
        localStorage.removeItem('auth-storage')
        localStorage.removeItem('userRole')
        localStorage.removeItem('adminRole')
        localStorage.removeItem('tutorRole')
        localStorage.removeItem('schoolRole')
        console.log('Auth Store: Reset complete, new state:', get())
      }
    }),
    {
      name: 'auth-storage',
      version: 2, // Increment version to force clean state
      partialize: (state) => ({ isLoggedIn: state.isLoggedIn, user: state.user }),
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error('Auth Store: Rehydration error:', error)
        } else {
          console.log('Auth Store: Rehydrating state:', state)
          const token = getAuthToken()
          if (state?.isLoggedIn && !token) {
            console.log('Auth Store: Stale session (no token), clearing login state')
            state.isLoggedIn = false
            state.user = null
          }
          if (state && state.user && state.isLoggedIn) {
            localStorage.setItem('userRole', state.user.role)
          } else {
            console.log('Auth Store: Invalid state detected, clearing localStorage')
            localStorage.removeItem('userRole')
            localStorage.removeItem('adminRole')
            localStorage.removeItem('tutorRole')
            localStorage.removeItem('schoolRole')
          }
        }
        useAuthStore.getState().setHasHydrated(true)
      },
      migrate: (persistedState: any, version: number) => {
        console.log('Auth Store: Migrating state from version:', version)
        if (version < 2) {
          // Clear old state format
          console.log('Auth Store: Clearing old state format')
          return { isLoggedIn: false, user: null }
        }
        return persistedState
      }
    }
  )
)

// Register at module load — useEffect in App can miss sync hydration (breaks footer + dashboard).
useAuthStore.persist.onFinishHydration(() => {
  useAuthStore.getState().setHasHydrated(true)
})

if (useAuthStore.persist.hasHydrated()) {
  useAuthStore.getState().setHasHydrated(true)
}
